import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveWorldview } from '../lib/storage';

const PROMPT = `Write a comprehensive .md file of my worldview:
# [Name]'s Brain
## How I Think + What I Value
(deepest frames, priors, reasoning style)
## My Views
(stances on domains — each with its why, tracing back to a frame above)
## What Formed Me
(experiences that produced the frames)
## What I Know
(facts, domain expertise)`;

type Step = 'choose' | 'paste' | 'saved';

interface CraftModalProps {
  open: boolean;
  onClose: () => void;
}

export function CraftModal({ open, onClose }: CraftModalProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('choose');
  const [pasted, setPasted] = useState('');
  const [copied, setCopied] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    if (open) return;
    const t = window.setTimeout(() => {
      setStep('choose');
      setPasted('');
      setCopied(false);
      setSavedId(null);
    }, 300);
    return () => window.clearTimeout(t);
  }, [open]);

  if (!open) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(PROMPT);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable; fall through silently
    }
  };

  const handleSave = () => {
    const content = pasted.trim();
    if (!content) return;
    const saved = saveWorldview(content);
    setSavedId(saved.id);
    setStep('saved');
  };

  const handleTestIt = () => {
    if (!savedId) return;
    navigate(`/worldview/chat/${savedId}`);
  };

  return (
    <div className="craft-modal" role="dialog" aria-modal="true" aria-label="Craft your worldview">
      <div className="craft-modal__backdrop" onClick={onClose} aria-hidden="true" />
      <div className="craft-modal__panel">
        <div className="craft-modal__body">
        <button
          type="button"
          className="craft-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        {step === 'choose' && (
          <>
            <p className="craft-modal__eyebrow">— Choose your path —</p>
            <h2 className="craft-modal__title">How will you craft?</h2>
            <p className="craft-modal__subtitle">
              Two paths to the same artefact: a portable file of how you reason.
            </p>

            <div className="craft-modal__options">
              <button
                type="button"
                className="craft-option"
                onClick={() => setStep('paste')}
              >
                <span className="craft-option__eyebrow">Self-Authored</span>
                <h3 className="craft-option__title">From the Hand</h3>
                <p className="craft-option__desc">
                  Take a prompt to the LLM you already use. Answer it honestly. Bring the result back here.
                </p>
                <span className="craft-option__cta">
                  Begin <span aria-hidden="true">→</span>
                </span>
              </button>

              <div className="craft-option craft-option--disabled" aria-disabled="true">
                <span className="craft-option__eyebrow craft-option__eyebrow--muted">
                  Coming Soon
                </span>
                <h3 className="craft-option__title">The Analyzer</h3>
                <p className="craft-option__desc">
                  An interview that climbs from your stated views to the principles beneath. Surfaces the generators you can&rsquo;t see yourself.
                </p>
                <span className="craft-option__cta craft-option__cta--muted">In the forge</span>
              </div>
            </div>
          </>
        )}

        {step === 'paste' && (
          <>
            <button
              type="button"
              className="craft-modal__back"
              onClick={() => setStep('choose')}
            >
              <span aria-hidden="true">←</span> Back
            </button>

            <p className="craft-modal__eyebrow">— Step One —</p>
            <h2 className="craft-modal__title">Take this to your LLM</h2>
            <p className="craft-modal__subtitle">
              Copy the prompt below, paste it into Claude or ChatGPT, then bring the result back.
            </p>

            <div className="craft-modal__prompt">
              <pre>{PROMPT}</pre>
              <button
                type="button"
                className="craft-modal__copy"
                onClick={handleCopy}
              >
                {copied ? 'Copied' : 'Copy prompt'}
              </button>
            </div>

            <p className="craft-modal__eyebrow">— Step Two —</p>
            <h2 className="craft-modal__title craft-modal__title--small">
              Paste your worldview
            </h2>

            <textarea
              className="craft-modal__textarea"
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
              placeholder="# Your name's brain..."
              rows={12}
              spellCheck={false}
            />

            <button
              type="button"
              className="craft-modal__save"
              onClick={handleSave}
              disabled={!pasted.trim()}
            >
              Save Worldview
            </button>
          </>
        )}

        {step === 'saved' && (
          <>
            <p className="craft-modal__eyebrow">— It is done —</p>
            <h2 className="craft-modal__title">Your worldview is forged</h2>
            <p className="craft-modal__subtitle">
              Stored locally on this device. Now test it — chat with an agent running your worldview and see if it reasons like you.
            </p>
            <div className="craft-modal__final-actions">
              <button
                type="button"
                className="craft-modal__save"
                onClick={handleTestIt}
                disabled={!savedId}
              >
                Test it <span aria-hidden="true">→</span>
              </button>
              <button
                type="button"
                className="craft-modal__back craft-modal__back--inline"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </>
        )}
        </div>
      </div>
    </div>
  );
}
